import React, { useMemo, useCallback } from 'react';
import { StyleSheet, View, Pressable } from 'react-native';
import useTheme from '@/hooks/useNeutralTheme';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useModalStore } from '@/store';
import {
  API_DAILY_LIMIT_PREMIUM_DISPLAY,
  PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY,
  PRICING_PLANS,
} from '@/constants/config';
import { trackEvent } from '@/constants/utils/analytics';

const createStyles = (colors) => StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    padding: 12,
    gap: 12,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
    backgroundColor: colors.solid_color_block,
  },
  rowHovered: {
    borderColor: colors.text_color_primary,
  },
  iconChip: {
    width: 30,
    height: 30,
    borderRadius: 9,
    backgroundColor: colors.text_color_primary,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  textContainer: {
    flex: 1,
  },
});

/** Compact Premium upsell row for the credit-store modal variants. */
function PremiumUpsellRow({ onClose }) {
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const { openModal, closeModal } = useModalStore(state => state.actions);

  const handlePress = useCallback(() => {
    trackEvent('conversion', 'premium_upsell_clicked', 'credits_modal_row');
    onClose?.();
    closeModal();
    // PricingModal detects the active Plus subscription and opens in its
    // Premium-upgrade state (Plus card hidden, Premium preselected).
    setTimeout(() => openModal('PricingModal'), 100);
  }, [onClose, closeModal, openModal]);

  return (
    <Pressable
      style={({ hovered }) => [styles.row, hovered && styles.rowHovered]}
      onPress={handlePress}
    >
      <View style={styles.iconChip}>
        <Icon name="gem" size={14} color={colors.accent_text_color} solid />
      </View>
      <View style={styles.textContainer}>
        <Text size="s" weight="semibold" color="primary">
          Or upgrade to Plus Premium
        </Text>
        <Text size="xs" color="secondary">
          {`${PLUS_PREMIUM_MONTHLY_CREDITS_DISPLAY} credits/mo + ${API_DAILY_LIMIT_PREMIUM_DISPLAY} API requests/day — $${PRICING_PLANS.plusPremium.annualMonthlyPrice}/mo billed annually`}
        </Text>
      </View>
      <Icon name="chevron-right" size={12} color={colors.text_color_tertiary} />
    </Pressable>
  );
}

export default React.memo(PremiumUpsellRow);
